import { Box, Grid } from '@mui/material';
import { styled, css, useTheme } from '@mui/material/styles';
import { ResponsiveBar } from '@nivo/bar';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ComplianceLoadingSkeleton from './loading-skeleton';
import { getDesignTokens } from '../../base/core';
import ExpandableStatusList, {
  StatusListItem,
} from '../../base/expandable-status-list';
import PieGraph, { GraphDatum } from '../../base/graph-card';
import { SectorDistributionDatum } from '../../base/graph-card/_type';
import JurisdictionChoroplethWrapper from '../../base/graph-card/jurisdiction-choropleth-wrapper';
import SectorDistributionChart from '../../base/graph-card/sector-distribution-chart';
import { AIicon } from '../../base/icon/ai';
import { ControlIcon } from '../../base/icon/control';
import { PageWindows } from '../../base/icon/page-windows-2';
import { TestIcon } from '../../base/icon/test';
import useSnackbarContext from '../../base/snackbar/snackbar-context';
import Spacer, { spc } from '../../base/spacer';
import Text from '../../base/text/index';
import { useAIResourcesContext } from '../../project/dashboard/ai-resources-context';
import DashboardDrillDownDrawer, {
  type DrawerContent,
} from '../../project/dashboard/drill-down-drawer';
import ProjectDetails from '../../project/dashboard/project-details';

import { getNivoChartTheme } from '~/client/components/assessment/helper';
import { useAuthorization } from '~/client/hooks/use-authorization';
import { useDashboardComponentsConfig } from '~/client/hooks/use-dashboard-components-config';
import * as GQL from '~/graphql/generated/client';
import Button from '~c/components/base/button';

interface Props {
  className?: string;
}

export interface ProjectData {
  id: string;
  projectName: string;
  source: string;
  risk: GQL.AssessmentRiskStatusType;
  assessmentCompletion: number;
  policyAlignment: GQL.AssessmentRiskStatusType;
  numControls: number;
  numTestScores: number;
  projectType?: GQL.ProjectType;
  securityStatus?: GQL.AssessmentCategoryStatus;
  privacyStatus?: GQL.AssessmentCategoryStatus;
  biasStatus?: GQL.AssessmentCategoryStatus;
  performanceStatus?: GQL.AssessmentCategoryStatus;
  assessmentStatusByPolicy?: {
    policyIdentifier: string;
    status: GQL.AssessmentStatusType | null;
  }[];
  modelDescription?: string;
  modelOwner?: string;
  modelArchitecture?: string;
  sharedWith?: GQL.SharedMembers[];
  /** In-progress creation-wizard draft — clicking it reopens the stepper. */
  isDraft?: boolean;
}

export interface ProjectListResponse {
  data: ProjectData[];
  totalCount: number;
}

interface AssuranceStatusCountsData {
  na: number;
  incomplete: number;
  pass: number;
  fail: number;
}

interface AssuranceDistributionData {
  security: AssuranceStatusCountsData;
  privacy: AssuranceStatusCountsData;
  bias: AssuranceStatusCountsData;
}

const AssuranceDistributionCard: React.FC<{
  data: AssuranceDistributionData;
  tokens: ReturnType<typeof getDesignTokens>;
}> = ({ data, tokens }) => {
  const theme = useTheme();
  const chartTheme = React.useMemo(() => getNivoChartTheme(theme), [theme]);

  const barData = [
    {
      category: 'Bias',
      Pass: data.bias.pass,
      Fail: data.bias.fail,
      Incomplete: data.bias.incomplete,
      Unverified: data.bias.na,
    },
    {
      category: 'Privacy',
      Pass: data.privacy.pass,
      Fail: data.privacy.fail,
      Incomplete: data.privacy.incomplete,
      Unverified: data.privacy.na,
    },
    {
      category: 'Security',
      Pass: data.security.pass,
      Fail: data.security.fail,
      Incomplete: data.security.incomplete,
      Unverified: data.security.na,
    },
  ];

  const statusColors: Record<string, string> = {
    Pass: tokens.status.success,
    Fail: tokens.status.danger,
    Incomplete: tokens.status.warning,
    Unverified: tokens.status.neutral,
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        padding: '24px',
        bgcolor: 'background.paper',
      }}
    >
      <Text sz="title" sx={{ mb: 2, display: 'block' }}>
        Assurance Status by Project
      </Text>
      <Box sx={{ height: 240 }}>
        <ResponsiveBar
          data={barData}
          keys={['Pass', 'Fail', 'Incomplete', 'Unverified']}
          indexBy="category"
          groupMode="grouped"
          layout="horizontal"
          margin={{ top: 10, right: 160, bottom: 30, left: 80 }}
          padding={0.25}
          innerPadding={3}
          colors={({ id }) => statusColors[id as string] ?? tokens.borderSubtle}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            format: (v) => (Number.isInteger(v) ? v : ''),
          }}
          enableLabel={false}
          theme={chartTheme}
          tooltip={({ id, value, indexValue }) => (
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                fontSize: '0.8125rem',
                boxShadow: theme.shadows[2],
              }}
            >
              <strong>{indexValue}</strong> — {id}: {value}
            </Box>
          )}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'right',
              direction: 'column',
              justify: false,
              translateX: 150,
              translateY: 0,
              itemsSpacing: 6,
              itemWidth: 130,
              itemHeight: 18,
              itemDirection: 'left-to-right',
              itemOpacity: 0.9,
              symbolSize: 10,
              symbolShape: 'circle',
            },
          ]}
        />
      </Box>
    </Box>
  );
};

interface AssessmentStatusCountsData {
  notStarted: number;
  inProgress: number;
  approved: number;
  other: number;
}

interface AssessmentStatusDistributionData {
  preScreening: AssessmentStatusCountsData;
  privacy: AssessmentStatusCountsData;
  safeguard: AssessmentStatusCountsData;
}

const AssessmentStatusDistributionCard: React.FC<{
  data: AssessmentStatusDistributionData;
  tokens: ReturnType<typeof getDesignTokens>;
}> = ({ data, tokens }) => {
  const theme = useTheme();
  const chartTheme = React.useMemo(() => getNivoChartTheme(theme), [theme]);

  const barData = [
    {
      category: 'Pre-Screening',
      'Not Started': data.preScreening.notStarted,
      'In Progress': data.preScreening.inProgress,
      Approved: data.preScreening.approved,
      Other: data.preScreening.other,
    },
    {
      category: 'Privacy',
      'Not Started': data.privacy.notStarted,
      'In Progress': data.privacy.inProgress,
      Approved: data.privacy.approved,
      Other: data.privacy.other,
    },
    {
      category: 'Safeguard',
      'Not Started': data.safeguard.notStarted,
      'In Progress': data.safeguard.inProgress,
      Approved: data.safeguard.approved,
      Other: data.safeguard.other,
    },
  ];

  const statusColors: Record<string, string> = {
    'Not Started': tokens.borderSubtle,
    'In Progress': tokens.status.warning,
    Approved: tokens.status.success,
    Other: tokens.status.danger,
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        padding: '24px',
        bgcolor: 'background.paper',
      }}
    >
      <Text sz="title" sx={{ mb: 2, display: 'block' }}>
        Assessment Status
      </Text>
      <Box sx={{ height: 240 }}>
        <ResponsiveBar
          data={barData}
          keys={['Not Started', 'In Progress', 'Approved', 'Other']}
          indexBy="category"
          groupMode="grouped"
          layout="horizontal"
          margin={{ top: 10, right: 160, bottom: 30, left: 100 }}
          padding={0.25}
          innerPadding={3}
          colors={({ id }) => statusColors[id as string] ?? tokens.borderSubtle}
          axisLeft={{ tickSize: 0, tickPadding: 10 }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            format: (v) => (Number.isInteger(v) ? v : ''),
          }}
          enableLabel={false}
          theme={chartTheme}
          tooltip={({ id, value, indexValue }) => (
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                fontSize: '0.8125rem',
                boxShadow: theme.shadows[2],
              }}
            >
              <strong>{indexValue}</strong> — {id}: {value}
            </Box>
          )}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'right',
              direction: 'column',
              justify: false,
              translateX: 150,
              translateY: 0,
              itemsSpacing: 6,
              itemWidth: 130,
              itemHeight: 18,
              itemDirection: 'left-to-right',
              itemOpacity: 0.9,
              symbolSize: 10,
              symbolShape: 'circle',
            },
          ]}
        />
      </Box>
    </Box>
  );
};

/**
 * Builds a `StatusListItem[]` for a slice-drill-down popover by filtering the
 * supplied projects, mapping each survivor to a row, and wiring its onClick to
 * navigate into the per-AI-system dashboard.
 *
 * Extracted so the two slice useMemos below (risk pie + compliance pie) share
 * one body — keeps them in lockstep and avoids the SonarLint S4144
 * "duplicate function body" warning that fires when the same closure is
 * inlined twice.
 *
 * Generic on the project shape (only `_id` and `name` are read) so the helper
 * stays decoupled from the specific GraphQL query type and works for any
 * future caller passing a structurally compatible list.
 */
function buildProjectStatusItems<P extends { _id: string; name: string }>(
  projects: readonly P[],
  filterFn: (p: P) => boolean,
  status: string,
  color: string,
  navigate: (path: string) => void
): StatusListItem[] {
  return projects.filter(filterFn).map((p) => ({
    name: p.name,
    status,
    color,
    onClick: () => navigate(`/ai-system/${p._id}/dashboard`),
  }));
}

const Compliance: React.FC<Props> = ({ className }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const authorization = useAuthorization();
  const tokens = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode]
  );

  const { visibility: dashboardVisibility } = useDashboardComponentsConfig();

  const {
    aiSystemListLoading: loading,
    aiSystemListError: error,
    refetchAiSystemList,
    organizationUnitId,
    aiSystemList,
  } = useAIResourcesContext();

  const [aiSystemData, setAiSystemsData] = React.useState<ProjectListResponse>({
    data: [],
    totalCount: 0,
  });
  const [projectRiskData, setProjectRiskData] = useState<GraphDatum[]>([]);
  const [policyAlignmentData, setPolicyAlignmentData] = useState<GraphDatum[]>(
    []
  );
  const [notAligned, setNotAligned] = useState(0);
  const [partiallyCompliant, setPartiallyCompliant] = useState(0);
  const [highRisk, setHighRisk] = useState(0);
  const [totalAISystems, setTotalAISystems] = useState(0);
  const [totalAIUseCases, setTotalAIUseCases] = useState(0);
  const [totalNumControls, setNumControls] = useState(0);
  const [totalNumTestScore, setNumTestScores] = useState(0);

  const Snackbar = useSnackbarContext();

  const {
    data: riskDistributionData,
    previousData: previousRiskDistributionData,
    loading: riskDistributionLoadingRaw,
    error: riskDistributionError,
  } = GQL.useAiSystemListForRiskDistributionQuery({
    variables: {
      organizationUnitId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: dashboardMetricsData,
    previousData: previousDashboardMetricsData,
    loading: dashboardMetricsLoadingRaw,
    error: dashboardMetricsError,
  } = GQL.useAiSystemListForDashboardMetricsQuery({
    variables: {
      organizationUnitId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: alignmentDistributionData,
    previousData: previousAlignmentDistributionData,
    loading: alignmentDistributionLoadingRaw,
    error: alignmentDistributionError,
  } = GQL.useAiSystemListForAlignmentDistributionQuery({
    variables: {
      organizationUnitId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: sectorDistributionData,
    previousData: previousSectorDistributionData,
    loading: sectorDistributionLoadingRaw,
    error: sectorDistributionError,
  } = GQL.useAiSystemListForSectorDistributionQuery({
    variables: {
      organizationUnitId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: assuranceDistributionData,
    previousData: previousAssuranceDistributionData,
    loading: assuranceDistributionLoadingRaw,
    error: assuranceDistributionError,
  } = GQL.useAiSystemAssuranceDistributionQuery({
    variables: {
      organizationUnitId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const resolvedAssuranceDistributionData =
    assuranceDistributionData ?? previousAssuranceDistributionData;

  const showAssessmentStatusChart =
    dashboardVisibility.showAssessmentStatusChart;

  const {
    data: assessmentStatusDistributionData,
    previousData: previousAssessmentStatusDistributionData,
    loading: assessmentStatusDistributionLoadingRaw,
    error: assessmentStatusDistributionError,
  } = GQL.useAiSystemAssessmentStatusDistributionQuery({
    variables: { organizationUnitId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !showAssessmentStatusChart,
  });

  const resolvedAssessmentStatusDistributionData =
    assessmentStatusDistributionData ??
    previousAssessmentStatusDistributionData;

  const resolvedRiskDistributionData =
    riskDistributionData ?? previousRiskDistributionData;
  const resolvedDashboardMetricsData =
    dashboardMetricsData ?? previousDashboardMetricsData;
  const resolvedAlignmentDistributionData =
    alignmentDistributionData ?? previousAlignmentDistributionData;
  const resolvedSectorDistributionData =
    sectorDistributionData ?? previousSectorDistributionData;

  const sectorChartData: SectorDistributionDatum[] = React.useMemo(() => {
    if (!resolvedSectorDistributionData?.aiSystemSectorDistribution) {
      return [];
    }
    return resolvedSectorDistributionData.aiSystemSectorDistribution.map(
      (item) => ({
        sector: item.sector,
        count: item.count,
      })
    );
  }, [resolvedSectorDistributionData?.aiSystemSectorDistribution]);

  const riskDistributionLoading =
    riskDistributionLoadingRaw && !resolvedRiskDistributionData;
  const dashboardMetricsLoading =
    dashboardMetricsLoadingRaw && !resolvedDashboardMetricsData;
  const alignmentDistributionLoading =
    alignmentDistributionLoadingRaw && !resolvedAlignmentDistributionData;
  const sectorDistributionLoading =
    sectorDistributionLoadingRaw && !resolvedSectorDistributionData;
  const assuranceDistributionLoading =
    assuranceDistributionLoadingRaw && !resolvedAssuranceDistributionData;
  const assessmentStatusDistributionLoading =
    showAssessmentStatusChart &&
    assessmentStatusDistributionLoadingRaw &&
    !resolvedAssessmentStatusDistributionData;

  const complianceLoading =
    loading ||
    riskDistributionLoading ||
    sectorDistributionLoading ||
    dashboardMetricsLoading ||
    alignmentDistributionLoading ||
    assuranceDistributionLoading ||
    assessmentStatusDistributionLoading;

  // Timeout: stop showing skeleton after 90s so user can retry if a query hangs
  const [loadTimedOut, setLoadTimedOut] = React.useState(false);
  React.useEffect(() => {
    if (!complianceLoading) {
      setLoadTimedOut(false);
      return undefined;
    }
    setLoadTimedOut(false);
    const timeoutId = window.setTimeout(() => setLoadTimedOut(true), 25000);
    return () => window.clearTimeout(timeoutId);
  }, [complianceLoading]);

  const dashboardQueryError =
    riskDistributionError?.message ??
    dashboardMetricsError?.message ??
    alignmentDistributionError?.message ??
    sectorDistributionError?.message ??
    assuranceDistributionError?.message ??
    assessmentStatusDistributionError?.message;

  React.useEffect(() => {
    if (error) {
      Snackbar.open(error);
    }
  }, [error]);

  React.useEffect(() => {
    if (dashboardQueryError) {
      Snackbar.open(dashboardQueryError);
    }
  }, [dashboardQueryError]);

  // Use aggregated dashboard metrics data instead of paginated data
  React.useEffect(() => {
    if (resolvedDashboardMetricsData?.aiSystemDashboardMetrics) {
      const metrics = resolvedDashboardMetricsData.aiSystemDashboardMetrics;
      setTotalAISystems(metrics.totalAISystems);
      setNumControls(metrics.totalControls);
      setNumTestScores(metrics.totalTests);
      setTotalAIUseCases(metrics.totalAIUseCases);
    }
  }, [resolvedDashboardMetricsData]);

  // Use aggregated risk distribution data for pie charts
  React.useEffect(() => {
    if (resolvedRiskDistributionData?.aiSystemRiskDistribution) {
      const { low, medium, high } =
        resolvedRiskDistributionData.aiSystemRiskDistribution;
      setHighRisk(high);
      setProjectRiskData([
        {
          id: 'Low',
          label: `${low} Low`,
          value: low,
          color: tokens.status.success,
        },
        {
          id: 'Med',
          label: `${medium} Med`,
          value: medium,
          color: tokens.status.warning,
        },
        {
          id: 'High',
          label: `${high} High`,
          value: high,
          color: tokens.status.danger,
        },
      ]);
    }
  }, [resolvedRiskDistributionData, tokens]);

  // Build "WHY" subtitle for the residual risk pie chart
  const riskBreakdownSubHeader = React.useMemo(() => {
    const dist = resolvedRiskDistributionData?.aiSystemRiskDistribution;
    if (!dist || dist.high === 0) {
      return undefined;
    }

    const parts: string[] = [];
    if (dist.highDueToTechRisk > 0) {
      parts.push(`${dist.highDueToTechRisk} due to technology risk`);
    }
    if (dist.highDueToAlignmentRisk > 0) {
      parts.push(`${dist.highDueToAlignmentRisk} due to policy alignment`);
    }
    return parts.length > 0 ? parts.join(', ') : undefined;
  }, [resolvedRiskDistributionData]);

  // Use aggregated alignment distribution data for policy alignment pie chart.
  // INTERIM LINKAGE: server now groups on cachedMetrics.riskStatus (residual
  // = worst-of(tech, alignment)), so this pie carries the same data as the
  // Project Risk pie above with compliance-themed labels. Brand-new projects
  // with no scored data yet still land in the "Not Started" bucket rather
  // than being mislabelled as Not Compliant.
  React.useEffect(() => {
    if (resolvedAlignmentDistributionData?.aiSystemAlignmentDistribution) {
      const {
        aligned,
        notAligned: notAlignedCount,
        partiallyCompliant: partiallyCompliantCount,
        unavailable: unavailableCount,
      } = resolvedAlignmentDistributionData.aiSystemAlignmentDistribution;
      setNotAligned(notAlignedCount);
      setPartiallyCompliant(partiallyCompliantCount);
      setPolicyAlignmentData([
        {
          id: 'Fully Compliant',
          label: `${aligned} Fully Compliant`,
          value: aligned,
          color: tokens.status.success,
        },
        {
          id: 'Partially Compliant',
          label: `${partiallyCompliantCount} Partially Compliant`,
          value: partiallyCompliantCount,
          color: tokens.status.warning,
        },
        {
          id: 'Not Compliant',
          label: `${notAlignedCount} Not Compliant`,
          value: notAlignedCount,
          color: tokens.status.danger,
        },
        {
          id: 'Not Started',
          label: `${unavailableCount} Not Started`,
          value: unavailableCount,
          color: tokens.status.neutral,
        },
      ]);
    }
  }, [resolvedAlignmentDistributionData, tokens]);

  // Build "WHY" subtitle for the policy compliance pie chart
  const alignmentBreakdownSubHeader = React.useMemo(() => {
    if (notAligned === 0 && partiallyCompliant === 0) {
      return undefined;
    }

    const parts: string[] = [];
    if (notAligned > 0) {
      parts.push(`${notAligned} not compliant`);
    }
    if (partiallyCompliant > 0) {
      parts.push(`${partiallyCompliant} partially compliant`);
    }
    return parts.join(', ');
  }, [notAligned, partiallyCompliant]);

  // Non-compliant project items for expandable lists
  const riskNonCompliantProjects = React.useMemo<StatusListItem[]>(() => {
    const projects = aiSystemList?.aiSystemList?.projects;
    if (!projects) {
      return [];
    }
    return projects
      .filter(
        (p) =>
          p.riskStatus === GQL.AssessmentRiskStatusType.High ||
          p.riskStatus === GQL.AssessmentRiskStatusType.Medium
      )
      .map((p) => {
        const isHigh = p.riskStatus === GQL.AssessmentRiskStatusType.High;
        return {
          name: p.name,
          status: isHigh ? 'High' : 'Medium',
          color: isHigh ? tokens.status.danger : tokens.status.warning,
          onClick: () => navigate(`/ai-system/${p._id}/dashboard`),
        };
      });
  }, [aiSystemList, tokens, navigate]);

  // INTERIM LINKAGE: bucketed by residual riskStatus, matching the data
  // source the Project Compliance pie now uses. Only HIGH (Not Compliant)
  // projects are included; Medium projects show in the "Partially Compliant"
  // pie slice and the verbal alignmentBreakdownSubHeader above. Mixing them
  // here would force the popover to be titled "Non-Compliant Projects"
  // while half the rows say "Partially Compliant".
  const alignmentNonCompliantProjects = React.useMemo<StatusListItem[]>(() => {
    const projects = aiSystemList?.aiSystemList?.projects;
    if (!projects) {
      return [];
    }
    return projects
      .filter((p) => p.riskStatus === GQL.AssessmentRiskStatusType.High)
      .map((p) => ({
        name: p.name,
        status: 'Not Compliant',
        color: tokens.status.danger,
        onClick: () => navigate(`/ai-system/${p._id}/dashboard`),
      }));
  }, [aiSystemList, tokens, navigate]);

  // Create risk status distribution data from the new optimized query

  // ── Pie-click drill-down: bucket projects by risk status per slice id ──
  // Mirrors the project-dashboard drawer UX so users can click any pie slice
  // and see the list of projects in that bucket.
  const projectsByRiskSlice = React.useMemo<
    Record<string, StatusListItem[]>
  >(() => {
    const projects = aiSystemList?.aiSystemList?.projects ?? [];
    return {
      Low: buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.Low,
        'Low',
        tokens.status.success,
        navigate
      ),
      Med: buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.Medium,
        'Medium',
        tokens.status.warning,
        navigate
      ),
      High: buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.High,
        'High',
        tokens.status.danger,
        navigate
      ),
    };
  }, [aiSystemList, tokens, navigate]);

  // Slice-click drill-down for the Compliance pie. INTERIM LINKAGE: buckets
  // projects by residual riskStatus (matching the pie's data source), with
  // compliance-themed labels. The "Not Started" slice surfaces projects
  // whose cached riskStatus is Unavailable or unset.
  const projectsByComplianceSlice = React.useMemo<
    Record<string, StatusListItem[]>
  >(() => {
    const projects = aiSystemList?.aiSystemList?.projects ?? [];
    return {
      'Fully Compliant': buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.Low,
        'Fully Compliant',
        tokens.status.success,
        navigate
      ),
      'Partially Compliant': buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.Medium,
        'Partially Compliant',
        tokens.status.warning,
        navigate
      ),
      'Not Compliant': buildProjectStatusItems(
        projects,
        (p) => p.riskStatus === GQL.AssessmentRiskStatusType.High,
        'Not Compliant',
        tokens.status.danger,
        navigate
      ),
      'Not Started': buildProjectStatusItems(
        projects,
        (p) =>
          !p.riskStatus ||
          p.riskStatus === GQL.AssessmentRiskStatusType.Unavailable,
        'Not Started',
        tokens.status.neutral,
        navigate
      ),
    };
  }, [aiSystemList, tokens, navigate]);

  const [drawerContent, setDrawerContent] =
    React.useState<DrawerContent | null>(null);

  const handleRiskSliceClick = React.useCallback(
    (datum: GraphDatum, _event: React.MouseEvent<SVGPathElement>) => {
      const key = String(datum.id); // 'Low' | 'Med' | 'High'
      const items = projectsByRiskSlice[key] ?? [];
      if (items.length === 0) {
        return;
      }
      const titleMap: Record<string, string> = {
        Low: 'Low Risk Projects',
        Med: 'Medium Risk Projects',
        High: 'High Risk Projects',
      };
      setDrawerContent({
        type: 'status-list',
        title: titleMap[key] ?? `${key} Risk Projects`,
        items,
      });
    },
    [projectsByRiskSlice]
  );

  const handleComplianceSliceClick = React.useCallback(
    (datum: GraphDatum, _event: React.MouseEvent<SVGPathElement>) => {
      const key = String(datum.id);
      const items = projectsByComplianceSlice[key] ?? [];
      if (items.length === 0) {
        return;
      }
      setDrawerContent({
        type: 'status-list',
        title: `${key} Projects`,
        items,
      });
    },
    [projectsByComplianceSlice]
  );

  /**
   * Render KPIs and charts. Shows a skeleton while the consolidated
   * dashboard query is still loading.
   */
  const renderDashboardContent = () => {
    if (loadTimedOut) {
      return (
        <>
          <Text sz="body16" color="secondary">
            Loading is taking longer than expected.
          </Text>
          <Spacer pb={spc.s} />
          <Button
            onClick={() => refetchAiSystemList()}
            color="primary"
            outlined
          >
            Retry
          </Button>
        </>
      );
    }
    if (complianceLoading) {
      return <ComplianceLoadingSkeleton />;
    }

    return (
      <>
        <div>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Spacer pb={spc.xs} />
              <div className="grid">
                <div>
                  <div className="details-grid">
                    <ProjectDetails
                      value={`${totalAISystems}`}
                      details="Total Projects"
                      icon={<AIicon />}
                    />
                    <ProjectDetails
                      value={`${Math.round(totalAIUseCases)}`}
                      details="Total AI Use Cases"
                      icon={<PageWindows />}
                    />
                    <ProjectDetails
                      value={`${Math.round(totalNumControls)}`}
                      details="Total Controls"
                      icon={<ControlIcon />}
                    />
                    <ProjectDetails
                      value={`${Math.round(totalNumTestScore)}`}
                      details="Total Tests"
                      icon={<TestIcon />}
                    />
                  </div>
                  {(dashboardVisibility.showRiskPie ||
                    dashboardVisibility.showCompliancePie) && (
                    <>
                      <Spacer pb={spc.xs} />
                      <div className="grid">
                        {dashboardVisibility.showRiskPie && (
                          <PieGraph
                            className="pie-graph"
                            data={projectRiskData}
                            title="Project Residual Risk"
                            subHeader={
                              <>
                                {riskBreakdownSubHeader}
                                {riskNonCompliantProjects.length > 0 && (
                                  <>
                                    {riskBreakdownSubHeader ? ' ' : ''}
                                    <ExpandableStatusList
                                      summary={
                                        riskBreakdownSubHeader
                                          ? `(${riskNonCompliantProjects.length} projects)`
                                          : highRisk > 0
                                            ? `${highRisk} projects at high risk`
                                            : 'No high risk projects'
                                      }
                                      items={riskNonCompliantProjects}
                                      popoverTitle="High Risk Projects"
                                      onTriggerClick={
                                        riskNonCompliantProjects.length > 0
                                          ? () => {
                                              setDrawerContent({
                                                type: 'status-list',
                                                title:
                                                  'High & Medium Risk Projects',
                                                items: riskNonCompliantProjects,
                                              });
                                            }
                                          : undefined
                                      }
                                    />
                                  </>
                                )}
                              </>
                            }
                            donutOpt={{
                              title: highRisk,
                              description: 'Projects High Risk',
                            }}
                            onSliceClick={handleRiskSliceClick}
                          />
                        )}
                        {dashboardVisibility.showCompliancePie && (
                          <PieGraph
                            title="Project Compliance"
                            subHeader={
                              <>
                                {alignmentBreakdownSubHeader}
                                {alignmentNonCompliantProjects.length > 0 && (
                                  <>
                                    {alignmentBreakdownSubHeader ? ' ' : ''}
                                    <ExpandableStatusList
                                      summary={
                                        alignmentBreakdownSubHeader
                                          ? `(${alignmentNonCompliantProjects.length} projects)`
                                          : notAligned > 0
                                            ? `${notAligned} projects not compliant`
                                            : 'All projects compliant'
                                      }
                                      items={alignmentNonCompliantProjects}
                                      popoverTitle="Non-Compliant Projects"
                                      onTriggerClick={
                                        alignmentNonCompliantProjects.length > 0
                                          ? () => {
                                              setDrawerContent({
                                                type: 'status-list',
                                                title: 'Non-Compliant Projects',
                                                items:
                                                  alignmentNonCompliantProjects,
                                              });
                                            }
                                          : undefined
                                      }
                                    />
                                  </>
                                )}
                              </>
                            }
                            data={policyAlignmentData}
                            donutOpt={{
                              title: notAligned,
                              description: 'Projects Not Compliant',
                            }}
                            onSliceClick={handleComplianceSliceClick}
                          />
                        )}
                      </div>
                    </>
                  )}
                  {(dashboardVisibility.showAssuranceChart &&
                    resolvedAssuranceDistributionData?.aiSystemAssuranceDistribution) ||
                  (showAssessmentStatusChart &&
                    resolvedAssessmentStatusDistributionData?.aiSystemAssessmentStatusDistribution) ? (
                    <>
                      <Spacer pb={spc.xs} />
                      <div className="grid">
                        {dashboardVisibility.showAssuranceChart &&
                          resolvedAssuranceDistributionData?.aiSystemAssuranceDistribution && (
                            <AssuranceDistributionCard
                              data={
                                resolvedAssuranceDistributionData.aiSystemAssuranceDistribution
                              }
                              tokens={tokens}
                            />
                          )}
                        {showAssessmentStatusChart &&
                          resolvedAssessmentStatusDistributionData?.aiSystemAssessmentStatusDistribution && (
                            <AssessmentStatusDistributionCard
                              data={
                                resolvedAssessmentStatusDistributionData.aiSystemAssessmentStatusDistribution
                              }
                              tokens={tokens}
                            />
                          )}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
        <Spacer py={spc.xs} />

        {dashboardVisibility.showJurisdictionMap &&
          (authorization.isSystemAdmin() ||
            authorization.hasOrgRole([
              'COMPLIANCE_MANAGER',
              'GOVERNANCE_MANAGER',
              'ORG_ADMIN',
            ])) && (
            <div className="grid">
              <JurisdictionChoroplethWrapper />
            </div>
          )}
        <Spacer py={spc.l} />
      </>
    );
  };

  return (
    <div className={className}>
      {renderDashboardContent()}
      <DashboardDrillDownDrawer
        content={drawerContent}
        onClose={() => setDrawerContent(null)}
      />
    </div>
  );
};

export default styled(Compliance)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    .details-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      ${theme.breakpoints.up('sm')} {
        gap: 24px;
        grid-template-columns: repeat(4, minmax(140px, 1fr));
      }
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 24px;
    }

    .MuiCard-root {
      border: 1px solid ${isDark ? t.borderSubtle : t.borderDefault};
    }
  `;
});
